import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Link, Redirect } from 'react-router-dom';
import BlogFooter from 'components/BlogFooter';
import { getBlogPost } from 'pages/public/Blogs/posts';
import { blogCanonicalUrl } from 'pages/public/Blogs/seo';
import MarkdownBody from './MarkdownBody';
import styles from './styles.module.scss';

const BlogArticle = ({ match }) => {
  const post = getBlogPost(match.params.slug);

  if (!post || !post.published || post.legacyComponent || !post.body) {
    return <Redirect to='/blog' />;
  }

  const articleUrl = blogCanonicalUrl(post.slug);

  return (
    <main className={styles.blogPost}>
      <Helmet>
        <title>{`${post.metaTitle} | Trivia`}</title>
        <meta name='description' content={post.metaDescription} />
        <meta property='og:title' content={post.metaTitle} />
        <meta property='og:description' content={post.metaDescription} />
        <meta property='og:url' content={articleUrl} />
        <meta property='og:image' content={post.image} />
        <link rel='canonical' href={articleUrl} />
      </Helmet>

      <header className={styles.navigation}>
        <div className={styles.navigationInner}>
          <Link className={styles.logo} to='/blog'>
            <span className={styles.logoMark}>?</span>
            <span>Trivia</span>
          </Link>
          <nav className={styles.navLinks} aria-label='Main navigation'>
            <a href='https://trytrivia.co/slack/'>Slack</a>
            <a href='https://trytrivia.co/microsoft-teams/'>Teams</a>
            <a href='https://trytrivia.co/'>Home</a>
            <Link className={styles.loginLink} to='/login'>
              Get started
            </Link>
          </nav>
        </div>
      </header>

      <article>
        <header className={styles.articleHeader}>
          <Link className={styles.backLink} to='/blog'>
            ← Back to all articles
          </Link>
          <p className={styles.category}>{post.category}</p>
          <h1>{post.title}</h1>
          <p className={styles.intro}>{post.excerpt}</p>
          <div className={styles.meta}>
            <span>{post.author}</span>
            <span>{post.date}</span>
            <span>{post.readTime}</span>
          </div>
        </header>

        <figure className={styles.articleImage}>
          <img src={post.image} alt={post.imageAlt} />
          <figcaption>
            {post.imageCredit ? `Photo: ${post.imageCredit}.` : null}
          </figcaption>
        </figure>

        <div className={styles.articleBody}>
          <MarkdownBody
            markdown={post.body}
            midImage={{
              src: post.inArticleImage,
              alt: post.inArticleAlt,
              caption: post.inArticleCaption,
              credit: post.inArticleCredit,
              className: styles.articleImage
            }}
          />

          {post.related?.length > 0 && (
            <div className={styles.related}>
              <p className={styles.category}>Related links</p>
              <div>
                {post.related.map((link) => (
                  <a href={link.href} key={link.href}>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <BlogFooter />
    </main>
  );
};

BlogArticle.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string
    })
  }).isRequired
};

export default BlogArticle;
